import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form941Schr = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 941 Schedule R: Updates, Instructions & Filing Dates"
          description="Form 941 Schedule R: Get the latest updates, who needs to file, step-by-step instructions, and key deadlines. E-file with TaxZerone for efficiency"
          keywords=""
          canonical="resources/employment-tax/form-941-schedule-r/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 941 </span>
          </span>{' '}
          Schedule R
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-schedule-r/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941 Schedule R at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <b>Form 941 Schedule R</b>
                        {' is used by '}
                        <b>aggregate filers</b>
                        {
                          ', such as Certified Professional Employer Organizations (CPEOs), IRS-approved agents, and certain third-party payers, to allocate the employment tax information reported on an aggregate '
                        }
                        <b>Form 941 or Form 941-X</b>
                        {' to each client.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Who Must File:</b>
                        {' IRS-approved agents and CPEOs '}
                        generally file
                        {' Schedule R with each aggregat'}
                        {
                          'e Form 941. Certain non-certified PEOs and third-party payers may also need to file Schedule R '
                        }
                        if clients claim the qualified small business payroll
                        tax credit for increasing research activities or other
                        credits on Form 941-X .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>When to File:</b>
                        {' Schedule R must '}
                        generally be
                        {' filed '}
                        <b>along with the aggregate Form 941</b>
                        {' each quarter. For 2026, the regular deadlines are '}
                        <b>
                          April 30, July 31, October 31 (November 2 in 2026),
                          and January 31, 2027
                        </b>
                        .
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      Form 941 Schedule R is essential for aggregate filers,
                      such as Certified Professional Employer Organizations
                      (CPEOs), IRS-approved agents, and other third-party
                      payers, to allocate aggregate information reported on{' '}
                      <Link
                        href="/resources/employment-tax/form-941-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 941
                      </Link>{' '}
                      or{' '}
                      <Link
                        href="/resources/employment-tax/form-941-x-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        941-X
                      </Link>{' '}
                      to each client.
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What’s new in Schedule R for 2024?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who Needs to File Schedule R?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schr" sx={{ color: '#0202ff' }}>
                          Instructions for completing Schedule R
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When to file?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What’s New in Schedule R for 2024?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The March 2024 revision includes:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    A checkbox to indicate if it is attached to Form 941 or
                    941-X.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Columns f, l, n, o, p, u, w, x, and y are used only with
                    <Link
                      href="/resources/employment-tax/form-941-x-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 941-X
                    </Link>
                    .
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The calendar year field is no longer pre-populated. This
                    version should be used primarily for quarters after December
                    31, 2023.
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who Needs to File Schedule R?
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Agents approved by the IRS under section 3504 and CPEOs must
                    complete Schedule R with each aggregate Form 941 filing.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Non-certified PEOs and other third-party payers must file
                    Schedule R if clients claim the qualified small business
                    payroll tax credit for increasing research activities or
                    other credits on Form 941-X.
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="schr"
                >
                  Instructions for Completing Schedule R
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-instruction-img.webp" />
                  <source srcSet="/assets/images/form-941-instruction-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-instruction-img.png"
                    loading="lazy"
                    alt="Form 941 Schedule R: EIN, Name, Type of filer"
                    width={764}
                    height={137}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Enter Your Business Information
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter your EIN and business name exactly as shown on the
                    attached Form 941 or 941-X.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Check the &quot;Type of filer&quot; box to indicate if
                    you’re a section 3504 agent, CPEO, or other third party.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Calendar Year
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the calendar year for Form 941 or 941-X to which
                    Schedule R is attached.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Check the box for the appropriate quarter to match the
                    quarter checked on the attached Form 941 or 941-X.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Form Checkbox
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    Indicate whether Schedule R is attached to Form 941 or
                    941-X.
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> When entering amounts over 999.99 on Schedule R,
                  don&apos;t enter commas.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Client and Employee Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-client-employee-img.webp" />
                  <source srcSet="/assets/images/form-941-client-employee-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-client-employee-img.png"
                    loading="lazy"
                    alt="Form 941 Schedule R: Client and Employee Information – columns a to i"
                    width={764}
                    height={150}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1">For each client report:</Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column a:</b> Client’s EIN.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column b (CPEO Use Only):</b> Enter a code for the type
                    of wages, tips, and compensation paid (codes A, B, C, or D).
                  </li>
                  <ul style={{ marginBottom: 20 }}>
                    <li style={{ listStyleType: 'none', marginBottom: 12 }}>
                      <b>Code summary :</b>
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      <b>A:</b> Wages, tips, and other compensation paid under
                      section 3511(a).{' '}
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      <b>B:</b> Wages, tips, and other compensation paid under
                      section 3511(c).
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      <b>C:</b> Wages, tips, and other compensation not reported
                      under code A or code B paid as a payor under a service
                      agreement described in Regulations section 31.3504-2(b)
                      (2).
                    </li>
                    <li>
                      <b>D:</b> Wages, tips, and other compensation paid as an
                      agent under Regulations section 31.3504-1.
                    </li>
                  </ul>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column c :</b> Number of employees who received wages,
                    tips, or other compensation (Form 941, line 1).
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column d :</b> Wages, tips, and other compensation
                    allocated (Form 941, line 2; Form 941-X, line 6, column 1).
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column e :</b> Total federal income tax withheld on Form
                    941, line 3 or (Form 941-X, line 7, column 1).
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column f :</b> Qualified sick leave and family leave
                    wages for leave taken after March 31, 2020, and before April
                    1, 2021 (Form 941-X, lines 9 and 10, column 1, total).
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column g :</b> Social security tax on wages and tips on
                    Form 941, lines 5a and 5b, column 2 or (Form 941-X, lines 8
                    and 11, column 1), multiplied by the applicable tax rate.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column h :</b> Medicare tax on Form 941, line 5c, column
                    2 or (Form 941-X, line 12, column 1), multiplied by the
                    applicable tax rate.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column i :</b> Total social security and Medicare taxes,
                    including Additional Medicare Tax withholding (Form 941,
                    line 5e).
                  </li>
                </ul>
                <picture>
                  <source srcSet="/assets/images/form-941-employee-colj-img.webp" />
                  <source srcSet="/assets/images/form-941-employee-colj-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-employee-colj-img.png"
                    loading="lazy"
                    alt="Form 941 Schedule R: Client and Employee Information – columns j to q"
                    width={764}
                    height={140}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column j:</b> Tax due on unreported tips from Form 941,
                    line 5f or (Form 941-X, line 14, column 1).
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column k:</b> Qualified small business payroll tax credit
                    from Form 941, line 11 or (Form 941-X, line 16, column 1).
                    Attach Form 8974 for each client claiming this credit.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column l:</b> Nonrefundable and refundable credit for
                    qualified sick and family leave wages paid between March 31,
                    2020, and April 1, 2021, from Form 941-X, lines 17 and 25,
                    column 1 total.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column n:</b> Nonrefundable and refundable credit for
                    qualified sick and family leave wages paid between March 31,
                    2021, and October 1, 2021, from Form 941-X, lines 18b and
                    26b, column 1, total.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column o:</b> Nonrefundable and refundable COBRA premium
                    assistance credit from Form 941-X, lines 18c and 26c, column
                    1, total.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column p:</b> Number of individuals provided COBRA
                    premium assistance from Form 941-X, line 18d, column 1.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column q:</b> Total taxes after adjustments and
                    nonrefundable credits from Form 941, line 12.
                  </li>
                </ul>
                <picture>
                  <source srcSet="/assets/images/form-941-employee-colr-y-img.webp" />
                  <source srcSet="/assets/images/form-941-employee-colr-y-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-employee-colr-y-img.png"
                    loading="lazy"
                    alt="Form 941 Schedule R: Client and Employee Information – columns r to y"
                    width={764}
                    height={141}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column r:</b> Total deposits, including overpayments
                    applied from prior quarters and current quarter overpayments
                    from Form 941, line 13. Include any payments made with the
                    return.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column u:</b> Total qualified health plan expenses for
                    sick and family leave wages paid between March 31, 2020, and
                    April 1, 2021, from Form 941-X, lines 28 and 29, column 1,
                    total.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column w:</b> Total qualified sick leave wages and
                    amounts under certain collectively bargained agreements for
                    leave taken between March 31, 2021, and October 1, 2021,
                    from Form 941-X, lines 35 and 37, column 1, total.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column x:</b> Total qualified health plan expenses for
                    sick and family leave wages from Form 941-X, lines 36 and
                    39, column 1, total.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Column y:</b> Total qualified family leave wages and
                    amounts under certain collectively bargained agreements for
                    leave taken between March 31, 2021, and October 1, 2021,
                    from Form 941-X, lines 38 and 40, column 1.
                  </li>
                  <ul style={{ listStyleType: 'disc' }}>
                    {' '}
                    <li style={{ marginBottom: 16 }}>
                      <b>Line 6:</b> Add lines 1 through 5 for each column (c
                      through y) to calculate the subtotals.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Line 7:</b> Enter the combined subtotals from line 9 of
                      all Continuation Sheets for Schedule R for each column (c
                      through y).
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Line 8:</b> Enter Form 941 amounts for your employees
                      for the same column (c through y). This line consolidates
                      any amounts for clients not reported individually on
                      Schedule R.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Line 9:</b> Add the totals of lines 6, 7, and 8 for
                      each column (c through y). Ensure that the totals for each
                      column match the aggregate amounts reported on Form 941.
                    </li>
                  </ul>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 3,
                    lineHeight: 1.5,
                  }}
                >
                  Continuation Sheet for Schedule R (Form 941)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-continuation-schr-img.webp" />
                  <source srcSet="/assets/images/form-941-continuation-schr-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-continuation-schr-img.png"
                    loading="lazy"
                    alt="Continuation Sheet for Schedule R (Form 941)"
                    width={767}
                    height={563}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Columns a through y.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  See the instructions above.
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 9:</b> Enter the subtotals for clients from lines 1
                    through 8 for columns c through l, n through r, u, and w
                    through y.
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="when"
                >
                  When to file?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  If you’re an aggregate{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 941
                  </Link>{' '}
                  filer, you must{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941-schedule-r/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Schedule R
                  </Link>{' '}
                  along with your aggregate Form 941 each quarter.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Quarter
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Reporting Period
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Quarter 1</TableCell>
                          <TableCell>JAN, FEB, MAR</TableCell>

                          <TableCell>April 30, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Quarter 2</TableCell>
                          <TableCell>APR, MAY, JUN</TableCell>

                          <TableCell>July 31, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Quarter 3</TableCell>
                          <TableCell>JUL, AUG, SEP</TableCell>

                          <TableCell>November 2, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Quarter4</TableCell>
                          <TableCell>OCT, NOV, DEC</TableCell>

                          <TableCell>February 1, 2027</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Agents and non-certified PEOs may{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 941
                  </Link>{' '}
                  and Schedule R electronically or by paper. Certified PEOs
                  (CPEOs) are generally required to file Form 941 and Schedule R
                  electronically.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
    </AppLayout>
  );
};

export default Form941Schr;
